import {
  FC,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import {
  BottomIcon,
  Button,
  CopyIcon,
  DotsMenuIcon,
  H1,
  H2,
  LocationIcon,
  Modal,
  OpenedEyeIcon,
  PosetiteliIcon,
  Separator,
  Tooltip,
  TopIcon,
} from '@smartshell/ui-kit';
import cn from 'classnames';

import { AuthContext } from 'auth';
import { useAdvertisementMutations, useGetAdvertisementContact } from 'hooks';
import { LocalizationContext } from 'localization';
import { openConfirmDialog } from 'services/Confirmation';
import {
  EAdvertisementStatus,
  IMyAdvertisement,
  IMyAdvertisementMetrics,
} from 'types';
import {
  copyToClipboard,
  formatCompactNumber,
  formatExpirationDate,
  formatSum,
} from 'utils';

import { AdvertisementUnpublishedModal } from '../AdvertisementUnpublishedModal';
import s from './AdvertisementViewModal.module.scss';
import { Slider } from './components';
import { IAdvertisementViewModalProps } from './interfaces';
import {
  getActionButton,
  getDropdownTextAction,
  getStatusLabel,
} from './utils';

export const AdvertisementViewModal: FC<IAdvertisementViewModalProps> = ({
  onClose,
  mode,
  advertisement,
  setAdvertisementManagerState,
  setAdvertisementViewState,
}) => {
  const { t, lang } = useContext(LocalizationContext);
  const {
    data: { permissions },
  } = useContext(AuthContext);
  const [isTextExpanded, setIsTextExpanded] = useState(false);
  const [shouldShowButton, setShouldShowButton] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLUListElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [openUnpublishedModal, setOpenUnpublishedModal] =
    useState<boolean>(false);

  const observerRef = useRef<ResizeObserver | null>(null);

  const textRef = useCallback((el: HTMLParagraphElement | null) => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }

    if (!el) return;

    const check = (): void => {
      const isOverflowing = el.scrollHeight > 210;
      setShouldShowButton(isOverflowing);
      if (!isOverflowing) {
        setIsTextExpanded(false);
      }
    };

    check();

    const observer = new ResizeObserver(check);
    observer.observe(el);
    observerRef.current = observer;
  }, []);

  const toggleTextExpand = (): void => {
    setIsTextExpanded(!isTextExpanded);
  };
  const toggleDropdown = (): void => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      const target = event.target as Node;

      const clickedInsideDropdown = dropdownRef.current?.contains(target);

      const clickedButton = buttonRef.current?.contains(target);

      if (!clickedInsideDropdown && !clickedButton) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  const {
    handledGetContact,
    contact,
    loading: showContactLoading,
  } = useGetAdvertisementContact();

  const {
    handledDeleteAdvertisement,
    deleting,
    handledChangeAdvertisementStatus,
    changingStatus,
    handledRepublishAdvertisement,
    republishing,
  } = useAdvertisementMutations(t);

  const disabled = !permissions?.has('advertisement_manage');

  return (
    <Modal className={s.container} onClose={onClose}>
      <div className={s.main}>
        {openUnpublishedModal && (
          <AdvertisementUnpublishedModal
            onClose={() => setOpenUnpublishedModal(false)}
            handledChangeAdvertisementStatus={handledChangeAdvertisementStatus}
            changingStatus={changingStatus}
            data={advertisement as IMyAdvertisement}
            closeViewModal={onClose}
          />
        )}
        <div className={s.left}>
          {advertisement?.images.length && (
            <Slider
              images={advertisement?.images}
              view={advertisement?.metrics?.views}
            />
          )}
        </div>
        <div className={s.right}>
          {mode === 'my' &&
            permissions?.has('advertisement_manage') &&
            getStatusLabel(t, lang, advertisement as IMyAdvertisement)}

          <div className={s.frame}>
            <div className={s.main_info}>
              <div className={s.title_block}>
                <H1 className={s.title}>{advertisement?.title}</H1>
                <span className={s.text_secondary}>
                  {advertisement?.category?.title} ·{' '}
                  {formatExpirationDate(advertisement?.published_at, lang)}
                </span>
              </div>

              {advertisement?.currency?.letter && (
                <span className={s.cost}>
                  {formatSum(
                    advertisement?.price,
                    advertisement?.currency?.letter
                  )}
                </span>
              )}

              <div className={s.geo}>
                <LocationIcon size="s" color="secondary" />
                <span className={s.text_secondary}>
                  {advertisement?.company?.city}
                </span>
              </div>

              {mode === 'my' && permissions?.has('advertisement_manage') ? (
                <>
                  <div className={s.contacts_block}>
                    <H2>{t('contacts')}</H2>
                    <div className={s.info}>
                      <span>
                        {(advertisement as IMyAdvertisement)?.contact}
                      </span>
                    </div>
                  </div>
                  <div className={s.statistics}>
                    <span className={s.title}>{t('statistics')}</span>
                    <div className={s.info}>
                      <div className={s.info_block}>
                        <div className={s.icon}>
                          <OpenedEyeIcon size="s" color="secondary" />
                        </div>
                        <div className={s.text_block}>
                          <span className={s.title}>{t('views')}</span>
                          <span className={s.value}>
                            {advertisement
                              ? formatCompactNumber(
                                  advertisement.metrics.views,
                                  t
                                )
                              : ''}
                          </span>
                        </div>
                      </div>
                      <div className={s.info_block}>
                        <div className={s.icon}>
                          <PosetiteliIcon size="s" color="secondary" />
                        </div>
                        <div className={s.text_block}>
                          <div className={cn(s.title, s.title_tooltip)}>
                            <span>{t('contacts.requests')}</span>
                            <Tooltip
                              position="top"
                              content={t('contacts.requests.tooltip')}
                            />
                          </div>
                          <span className={s.value}>
                            {advertisement
                              ? formatCompactNumber(
                                  (
                                    advertisement?.metrics as IMyAdvertisementMetrics
                                  )?.request_contact,
                                  t
                                )
                              : ''}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {!contact && (
                    <div className={s.contacts}>
                      <H2>{t('contacts')}</H2>
                      <Button
                        onClick={() => {
                          if (advertisement) {
                            handledGetContact(advertisement?.id);
                          }
                        }}
                        isLoading={showContactLoading}
                        className={s.button}
                      >
                        {t('show')}
                      </Button>
                    </div>
                  )}

                  {contact && !showContactLoading && (
                    <div className={s.contacts_view}>
                      <H2>{t('contacts')}</H2>
                      <div className={s.info}>
                        <span>{contact}</span>
                        <div
                          className={s.copy}
                          onClick={() => copyToClipboard(contact)}
                        >
                          <CopyIcon color="secondary" size="s" />
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          <div className={s.frame}>
            <span className={s.text_secondary}>
              {t('advertisement.manager.form.description')}
            </span>
            <div
              className={s.textWrapper}
              style={{ maxHeight: isTextExpanded ? 'none' : '210px' }}
            >
              <p ref={textRef} className={s.text}>
                {advertisement?.description}
              </p>
            </div>

            {shouldShowButton && (
              <button className={s.show_button} onClick={toggleTextExpand}>
                <span>{isTextExpanded ? t('hidden') : t('show.more')}</span>
                {isTextExpanded ? <TopIcon /> : <BottomIcon />}
              </button>
            )}
          </div>

          {mode === 'my' && permissions?.has('advertisement_manage') ? (
            <div className={cn(s.vendor, s.vendor_my)}>
              <span className={s.vendor_text}>
                {advertisement?.company?.name}
              </span>
              <span className={s.text_secondary}>
                {(advertisement as IMyAdvertisement)?.company?.city}
              </span>
            </div>
          ) : (
            <div className={s.vendor}>
              <span className={s.text_secondary}>{t('vendor')}</span>
              <span className={s.vendor_text}>
                {advertisement?.company?.name}
              </span>
            </div>
          )}
        </div>
      </div>
      {mode === 'my' && permissions?.has('advertisement_manage') && (
        <div className={s.footer}>
          {getActionButton(
            t,
            setAdvertisementManagerState,
            setAdvertisementViewState,
            setOpenUnpublishedModal,
            handledRepublishAdvertisement,
            advertisement as IMyAdvertisement,
            deleting || changingStatus || republishing,
            onClose,
            disabled
          )}
          <div className={s.actions}>
            <button
              className={s.button}
              ref={buttonRef}
              disabled={!permissions?.has('advertisement_manage')}
              onClick={toggleDropdown}
            >
              <DotsMenuIcon color="link-info" />
            </button>
            {isDropdownOpen && (
              <ul className={cn(s.dropdown)} ref={dropdownRef}>
                {(advertisement as IMyAdvertisement)?.status !==
                  EAdvertisementStatus.REJECTED &&
                  (advertisement as IMyAdvertisement)?.status !==
                    EAdvertisementStatus.MODERATION && (
                    <li
                      className={s.item}
                      onClick={(e) => {
                        e.stopPropagation();

                        if (
                          (advertisement as IMyAdvertisement)?.status ===
                          'ACTIVE'
                        ) {
                          setOpenUnpublishedModal(true);
                        } else if (
                          (advertisement as IMyAdvertisement)?.status ===
                            EAdvertisementStatus.COMPLETED ||
                          (advertisement as IMyAdvertisement)?.status ===
                            EAdvertisementStatus.REJECTED ||
                          (advertisement as IMyAdvertisement)?.status ===
                            EAdvertisementStatus.EXPIRED
                        ) {
                          handledRepublishAdvertisement(
                            (advertisement as IMyAdvertisement)?.id
                          ).then(() => onClose());
                        }
                      }}
                    >
                      {getDropdownTextAction(
                        t,
                        (advertisement as IMyAdvertisement)?.status
                      )}
                    </li>
                  )}
                <li
                  className={s.item}
                  onClick={(e) => {
                    e.stopPropagation();
                    setAdvertisementViewState(null);

                    setAdvertisementManagerState({
                      mode: 'edit',
                      advertisement: advertisement as IMyAdvertisement,
                    });
                  }}
                >
                  {t('edit')}
                </li>
                <Separator />
                <li
                  className={cn(s.item, s.red)}
                  onClick={(e) => {
                    e.stopPropagation();

                    if (advertisement) {
                      openConfirmDialog(t, 'remove', {
                        title: t('advertisement.delete.title'),
                        message: t(
                          'advertisement.delete.description',
                          advertisement?.title
                        ),
                        acceptTitle: t('delete'),
                        declineTitle: t('cancel'),
                      }).then(({ status }) => {
                        if (status === 'ACCEPTED') {
                          handledDeleteAdvertisement(advertisement?.id).then(
                            () => {
                              onClose();
                            }
                          );
                        }
                      });
                    }
                  }}
                >
                  {t('delete')}
                </li>
              </ul>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
};
